import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  ELEMENT_TRANSFORMERS,
  ElementTransformer,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  TextMatchTransformer,
  Transformer,
} from '@lexical/markdown'
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table'
import { $isParagraphNode, $isTextNode, LexicalNode, $createTextNode, ParagraphNode, ElementNode, TextNode } from 'lexical'

import { $createImageNode, $isImageNode, ImageNode } from '../../nodes/ImageNode'
import { getPureAltText, getImageSizes } from '../../utils'
import { isTableDividerRow } from './is-table-divider-row'

const IMAGE: TextMatchTransformer = {
  dependencies: [ImageNode],
  export: node => {
    if (!$isImageNode(node)) {
      return null
    }

    const { altText, height, width, src } = node.getFullData()

    const pureAltText = getPureAltText(altText)

    return `![${pureAltText}{${width}x${height}}](${src})`
  },
  importRegExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))/,
  regExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))$/,
  replace: (textNode, match) => {
    const [, altText, src] = match
    const { height, width } = getImageSizes(altText)

    const imageNode = $createImageNode({
      altText,
      src,
      height,
      width,
    })
    textNode.replace(imageNode)
  },
  trigger: ')',
  type: 'text-match',
}

const TABLE_ROW_REG_EXP = /^(?:\|)(.+)(?:\|)\s?$/

const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) {
      return null
    }

    const output: string[] = []

    const nodeChildren = node.getChildren()

    for (let rowIndex = 0; rowIndex < nodeChildren.length; rowIndex++) {
      const row = nodeChildren[rowIndex]

      const rowOutput = []
      if (!$isTableRowNode(row)) {
        continue
      }

      for (const cell of row.getChildren()) {
        // It's TableCellNode so it's just to make flow happy
        if ($isTableCellNode(cell)) {
          rowOutput.push($convertToMarkdownString(ALL_TRANSFORMERS, cell).replace(/\n/g, '\\n'))
        }
      }

      output.push(`|${rowOutput.join('|')}|`)
      if (rowIndex === 0) {
        // ROW FOR SEPARATE TABLE HEADER AND REST OF ROWS
        output.push(`|${rowOutput.map(_ => '---').join('|')}|`)
      }
    }

    return output.join('\n')
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _1, match) => {
    // Header row
    if (isTableDividerRow(match[0])) {
      const table = parentNode.getPreviousSibling()
      if (!table || !$isTableNode(table)) {
        return
      }

      const rows = table.getChildren()
      const lastRow = rows[rows.length - 1]
      if (!lastRow || !$isTableRowNode(lastRow)) {
        return
      }

      // Add header state to row cells
      lastRow.getChildren().forEach(cell => {
        if (!$isTableCellNode(cell)) {
          return
        }
        cell.toggleHeaderStyle(TableCellHeaderStates.ROW)
      })

      // Remove line
      parentNode.remove()
      return
    }

    const matchCells = mapToTableCells(match[0])

    if (matchCells == null) {
      return
    }

    const rows = [matchCells]
    let sibling = parentNode.getPreviousSibling()
    let maxCells = matchCells.length

    while (sibling) {
      if (!$isParagraphNode(sibling)) {
        break
      }

      if (sibling.getChildrenSize() !== 1) {
        break
      }

      const firstChild = sibling.getFirstChild()

      if (!$isTextNode(firstChild)) {
        break
      }

      const cells = mapToTableCells(firstChild.getTextContent())

      if (cells == null) {
        break
      }

      maxCells = Math.max(maxCells, cells.length)
      rows.unshift(cells)
      const previousSibling = sibling.getPreviousSibling()
      sibling.remove()
      sibling = previousSibling
    }

    const table = $createTableNode()

    for (const cells of rows) {
      const tableRow = $createTableRowNode()
      table.append(tableRow)

      for (let i = 0; i < maxCells; i++) {
        tableRow.append(i < cells.length ? cells[i] : createTableCell(''))
      }
    }

    const previousSibling = parentNode.getPreviousSibling()
    if ($isTableNode(previousSibling) && getTableColumnsSize(previousSibling) === maxCells) {
      previousSibling.append(...table.getChildren())
      parentNode.remove()
    } else {
      parentNode.replace(table)
    }

    table.selectEnd()
  },
  type: 'element',
}

const getTableColumnsSize = (table: TableNode): number => {
  const row = table.getFirstChild()
  return $isTableRowNode(row) ? row.getChildrenSize() : 0
}

const createTableCell = (textContent: string): TableCellNode => {
  textContent = textContent.replace(/\\n/g, '\n')
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS)
  $convertFromMarkdownString(textContent, ALL_TRANSFORMERS, cell)
  return cell
}

const mapToTableCells = (textContent: string): Array<TableCellNode> | null => {
  const match = textContent.match(TABLE_ROW_REG_EXP)
  if (!match || !match[1]) {
    return null
  }
  return match[1].split('|').map(text => createTableCell(text))
}

const NEW_LINE_EXPORTER: ElementTransformer = {
  dependencies: [ParagraphNode],
  export: node => {
    const elementNode = node as ElementNode
    if ($isParagraphNode(elementNode) && !elementNode?.getTextContent() && !elementNode?.getChildrenSize()) {
      return '&nbsp;'
    }
    return null
  },
  regExp: /^$/,
  replace: () => null,
  type: 'element',
}

const NEW_LINE_IMPORTER: ElementTransformer = {
  dependencies: [ParagraphNode],
  export: () => null,
  regExp: /&nbsp;/,
  replace: textNode => {
    textNode.append($createTextNode())
  },
  type: 'element',
}

const GREATER_LESSER_IMPORTER: TextMatchTransformer = {
  dependencies: [TextNode],
  export: () => null,
  importRegExp: /&lt;|&gt;/,
  regExp: /[<>]/g,
  replace: (textNode, match) => {
    const matchedText = match[0]

    if (matchedText === '&lt;') {
      textNode.replace($createTextNode('<'))
    } else if (matchedText === '&gt;') {
      textNode.replace($createTextNode('>'))
    }
  },
  trigger: ')',
  type: 'text-match',
}

export const ALL_TRANSFORMERS: Array<Transformer> = [
  TABLE,
  IMAGE,
  CHECK_LIST,
  NEW_LINE_EXPORTER,
  NEW_LINE_IMPORTER,
  GREATER_LESSER_IMPORTER,
  ...ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
]
